import LaunchIcon from "@mui/icons-material/Launch";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { runTWUDATFTOBidAward } from "../../api/bidAward";
import { getPresignedUrl, uploadFileToS3 } from "../../api/system.js";
import { selectUserSession } from "../../slices/userSession/userSessionSlice.js";
import { clearMTOBidAwardData, processMTOBidAwardPayload } from "../../util/bid/bidAward";
import { getS3ObjectName, isAwardedBid, longPollingBidFetch } from "../../util/bid/bidCommon.js";
import {
  BID_AWARDED_FILTER_OPTIONS,
  BID_STATUS,
  TWU_DAT_FTO_BID_AWARD_TYPE,
  TWU_DAT_FTO_BID_TIME_OFF_TYPES,
} from "../../util/bid/bidConstants";
import { buildAllotmentsPayload, buildTimeOffBidS3Data } from "../../util/bid/bidEventBuilder";
import { getEligibleEmployees } from "../../util/bid/bidGroupUtils";
import { getTotalAllotments } from "../../util/bid/timeOffBidUtils";
import {
  getDATFTOColumns,
  getEligibleEmployeesWithInvalidBalanceCount,
  getUnAwardedTableColumns,
  hasEmployeesMarkedNS,
} from "../../util/bid/TWUDATFTOBidUtils";
import { validateAllotments } from "../../util/bid/TWUDATFTOBidValidations";
import { copyJsonObject } from "../../util/commonUtils";
import { BID_AWARD_NO_BID_PAGE_TYPE } from "../../util/constants";
import { bidDataErrorCodeToErrorMessageMap } from "../../util/errorMapper";
import { TWU_DAT_FTO_BID_PUBLISH_DIALOG_MESSAGE } from "../../util/messageConstants";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay";
import customLoadingTableOverlay from "../CustomLoadingTableOverlay";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay";
import ErrorMessage from "../ErrorMessage";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import StarsCloseIconButton from "../fundamentals/StarsCloseIconButton";
import SuccessDialog from "../fundamentals/SuccessDialog";
import TWUDATFTOBidAwardExport from "./TWUDATFTOBidAwardExport";

const TWUDATFTOBidAward = (props) => {
  const [isAddEmployeeConfirmationChecked, setIsAddEmployeeConfirmationChecked] = useState(false);
  const [isRemoveEmployeeConfirmationChecked, setIsRemoveEmployeeConfirmationChecked] = useState(false);
  const [isEligibilityEmployeeConfirmationChecked, setIsEligibilityEmployeeConfirmationChecked] = useState(false);
  const [isEligibleEmployeeBalanceAdded, setIsEligibleEmployeeBalanceAdded] = useState(false);
  const [isAwardRunning, setIsAwardRunning] = useState(false);

  const [allotmentErrors, setAllotmentErrors] = useState("");
  const [awardErrorMessage, setAwardErrorMessage] = useState("");
  const [filteredDATFTOAwardRows, setFilteredDATFTOAwardRows] = useState([]);
  const [filteredDATFTOUnawardedRows, setFilteredDATFTOUnawardedRows] = useState([]);
  const [isEnlargeBidAllotmentsDialogOpen, setIsEnlargeBidAllotmentsDialogOpen] = useState(false);
  const [selectedAwardFilterCheckBoxList, setSelectedAwardFilterCheckBoxList] = useState(
    Object.values(TWU_DAT_FTO_BID_AWARD_TYPE)
  );
  const [selectedUnawardedFilterCheckBoxList, setSelectedUnawardedFilterCheckBoxList] = useState(
    Object.values(TWU_DAT_FTO_BID_AWARD_TYPE).filter(
      (type) =>
        type !== TWU_DAT_FTO_BID_AWARD_TYPE.AUTO_AWARDED && type !== TWU_DAT_FTO_BID_AWARD_TYPE.PARTIALLY_AWARDED
    )
  );
  const [selectedTimeOffTypeFilterCheckBoxList, setSelectedTimeOffTypeFilterCheckBoxList] = useState(
    Object.values(TWU_DAT_FTO_BID_TIME_OFF_TYPES)
  );

  const [selectedAwardFilterRadioButton, setSelectedAwardFilterRadioButton] = useState(BID_AWARDED_FILTER_OPTIONS.ALL);
  const [isReRunAwardWarningDialogOpen, setIsReRunAwardWarningDialogOpen] = useState(false);
  const [isPublishConfirmationDialogOpen, setIsPublishConfirmationDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [showPublishLoader, setShowPublishLoader] = useState(false);
  const [publishErrors, setPublishErrors] = useState("");
  const userSession = useSelector(selectUserSession);

  useEffect(() => {
    if (props.schedMonth !== "" && props.schedYear !== "") {
      setAllotmentErrors(
        validateAllotments(
          props.bidGroup,
          getTotalAllotments(props.allotmentDayState),
          props.calcDailyAllotments,
          props.schedMonth,
          props.schedYear
        )
      );
    }
  }, [props.allotmentDayState, props.calcDailyAllotments, props.bidGroup, props.schedMonth, props.schedYear]);

  useEffect(() => {
    const filterByRadioButtons = (filterValue) => {
      let filteredRows = [];
      switch (filterValue) {
        case BID_AWARDED_FILTER_OPTIONS.ALL:
          filteredRows = props.datFtoAwardRows;
          break;
        case BID_AWARDED_FILTER_OPTIONS.AWARDED:
          filteredRows = props.datFtoAwardRows.filter((row) => row.eid);
          break;
        case BID_AWARDED_FILTER_OPTIONS.UNAWARDED:
          filteredRows = props.datFtoAwardRows.filter((row) => !row.eid);
          break;
        default:
          console.error("Unrecognized filter option");
      }
      setFilteredDATFTOAwardRows(filteredRows);
      return filteredRows;
    };

    const filterByTimeOffTypeCheckBoxes = (filteredRows, timeOffTypeList) => {
      const awardTypeFilteredRows = filteredRows.filter((line) =>
        line.eid
          ? line.awardedDays?.some(
              (awardedDay) => awardedDay.date === line.date && timeOffTypeList.includes(awardedDay.timeOffType)
            )
          : true
      );
      setFilteredDATFTOAwardRows(awardTypeFilteredRows);
      return awardTypeFilteredRows;
    };

    const filterByAwardTypeCheckBoxes = (filteredRows, awardTypeList) => {
      const awardTypeFilteredRows = filteredRows.filter((line) =>
        line.eid ? awardTypeList.includes(line.awardType) : true
      );
      setFilteredDATFTOAwardRows(awardTypeFilteredRows);
      return awardTypeFilteredRows;
    };

    let filteredRows = filterByRadioButtons(selectedAwardFilterRadioButton);
    filteredRows = filterByTimeOffTypeCheckBoxes(filteredRows, selectedTimeOffTypeFilterCheckBoxList);
    filteredRows = filterByAwardTypeCheckBoxes(filteredRows, selectedAwardFilterCheckBoxList);
    setFilteredDATFTOAwardRows(filteredRows);
  }, [
    props.datFtoAwardRows,
    selectedAwardFilterRadioButton,
    selectedTimeOffTypeFilterCheckBoxList,
    selectedAwardFilterCheckBoxList,
  ]);

  useEffect(() => {
    const unawardedTypeFilteredRows = props.unAwardedList?.filter((line) =>
      line.eid ? selectedUnawardedFilterCheckBoxList.includes(line.awardType) : true
    );
    setFilteredDATFTOUnawardedRows(unawardedTypeFilteredRows);
  }, [selectedUnawardedFilterCheckBoxList, props.unAwardedList]);

  const isBidAwardAvailable = () => {
    for (const emp of props.bidGroup) {
      if ("awardType" in emp) {
        return true;
      }
    }
    return false;
  };

  const handleAwardFilterRadioButtonClicked = (event) => {
    setSelectedAwardFilterRadioButton(event.target.value);
  };

  const handleRunAwardClicked = (allotmentDayState, bidGroup) => {
    setIsAwardRunning(true);
    runTWUDATFTOBidAward(props.bid.referenceNumber)
      .then((res) => res.json())
      .then((response) => {
        const awardResult = processMTOBidAwardPayload(response, allotmentDayState, bidGroup);
        props.setBid({ ...props.bid, awardLog: awardResult.awardLog });
        props.setAllotmentDayState(awardResult.allotmentDayState);
        props.setInitialAllotmentDayState(awardResult.allotmentDayState);
        props.setInitialBidGroup(awardResult.bidGroup);
        props.setBidGroup(awardResult.bidGroup);
        setIsAwardRunning(false);
      })
      .catch((error) => {
        console.log("Error: ", error);
        setIsAwardRunning(false);
        setAwardErrorMessage("Unexpected Error");
      });
  };

  const renderBidGroupChangesDetectedAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Bid Group changes must be saved before you can run the award. This will delete any existing award results.
        </Alert>
      </Grid>
    );
  };

  const renderBidAllotmentsChangesDetectedAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Bid Allotment changes must be saved before you can run the award. This will delete the existing award results.
        </Alert>
      </Grid>
    );
  };

  const renderInvalidBalanceEmployeesAlert = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          Eligible Employees with invalid balances found. Please update the balances before running the award.
        </Alert>
      </Grid>
    );
  };

  const renderPreAwardChecks = () => {
    return (
      <>
        {/* Title */}
        <Grid container sx={{ padding: "10px" }}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
            >
              {props.bid.station} | {props.bid.workgroup}
              {props.schedMonth !== "" &&
                props.schedYear !== "" &&
                ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
              {props.bidName && ` | ${props.bidName}`}
            </Typography>
          </Grid>
        </Grid>

        {/* Bid Allotment/Bid Group Counts Section */}
        <Grid container sx={{ bgcolor: "secondary.swaLightestBlue", padding: "10px" }} alignItems={"center"}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "left" }}>
            <Box sx={{ padding: "5px" }}>
              <Grid container>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={5} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Allotments
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography
                        component="span"
                        sx={{
                          color: allotmentErrors ? "primary.swaRed" : "",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {getTotalAllotments(props.allotmentDayState)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={5} sx={{ textAlign: "left" }}>
                      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "bold" }}>
                        Eligible
                      </Typography>
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: "left" }}>
                      Total:{" "}
                      <Typography
                        component="span"
                        sx={{
                          color: allotmentErrors ? "primary.swaRed" : "",
                          fontSize: "14px",
                          fontWeight: "bold",
                        }}
                      >
                        {getEligibleEmployees(props.bidGroup).length}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {allotmentErrors === "" ? (
          <>
            {/* Checklist */}
            <Grid container sx={{ padding: "1rem", textAlign: "left" }}>
              <Grid item xs={12}>
                <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>
                  Check off each item as complete before running the Award:
                </Typography>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAddEmployeeConfirmationChecked}
                        onChange={(event) => setIsAddEmployeeConfirmationChecked(event.target.checked)}
                        name="addEmployeeCheck"
                        color="primary"
                      />
                    }
                    label="Add any Employees who entered the Workgroup during the Bid Window"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isRemoveEmployeeConfirmationChecked}
                        onChange={(event) => setIsRemoveEmployeeConfirmationChecked(event.target.checked)}
                        name="removeEmployeeCheck"
                        color="primary"
                      />
                    }
                    label="Remove any Employees who left the Workgroup after the Bid opened"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isEligibilityEmployeeConfirmationChecked}
                        onChange={(event) => setIsEligibilityEmployeeConfirmationChecked(event.target.checked)}
                        name="eligibilityEmployeeCheck"
                        color="primary"
                      />
                    }
                    label="Check eligibility of all Employees"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <FormControl required component="fieldset">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isEligibleEmployeeBalanceAdded}
                        onChange={(event) => setIsEligibleEmployeeBalanceAdded(event.target.checked)}
                        name="balancesCheck"
                        color="primary"
                      />
                    }
                    label="Add balances for all eligible Employees"
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ padding: "10px" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleRunAwardClicked(props.allotmentDayState, props.bidGroup)}
                  size="large"
                  sx={{ width: "200px" }}
                  startIcon={isAwardRunning ? null : <ListAltOutlinedIcon />}
                  disabled={
                    !isAddEmployeeConfirmationChecked ||
                    !isRemoveEmployeeConfirmationChecked ||
                    !isEligibilityEmployeeConfirmationChecked ||
                    !isEligibleEmployeeBalanceAdded ||
                    isAwardRunning ||
                    props.isBidGroupTabChanged ||
                    props.isBidAllotmentsTabChanged ||
                    getEligibleEmployeesWithInvalidBalanceCount(props.bidGroup) > 0
                  }
                >
                  {isAwardRunning ? <CircularProgress size={25} /> : "Run Award"}
                </Button>
              </Grid>
              {awardErrorMessage !== "" && (
                <ErrorMessage
                  errorMessage={bidDataErrorCodeToErrorMessageMap.bidAwardError.message}
                  errorCode={bidDataErrorCodeToErrorMessageMap.bidAwardError.code}
                />
              )}
            </Grid>
          </>
        ) : (
          <Grid container sx={{ padding: "1rem", textAlign: "left" }}>
            <Grid item xs={12}>
              <Typography sx={{ color: "primary.swaBlue", fontWeight: "bold" }}>
                There are not enough allotments.
              </Typography>
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  const handleAwardFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedAwardFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedAwardFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleUnawardedFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedUnawardedFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedUnawardedFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleTimeOffTypeFilterCheckBoxClicked = (event) => {
    let newFilterCheckBoxList = copyJsonObject(selectedTimeOffTypeFilterCheckBoxList);
    if (!event.target.checked) {
      newFilterCheckBoxList = newFilterCheckBoxList.filter((filter) => filter !== event.target.value);
    } else {
      newFilterCheckBoxList.push(event.target.value);
    }
    setSelectedTimeOffTypeFilterCheckBoxList(newFilterCheckBoxList);
  };

  const handleEnlargeBidAllotmentsButtonClicked = (event) => {
    setIsEnlargeBidAllotmentsDialogOpen(true);
  };

  const handleEnlargeBidAllotmentsDialogClosed = (event) => {
    setIsEnlargeBidAllotmentsDialogOpen(false);
  };

  const renderDATFTOs = (isDialog) => {
    return (
      <Grid container sx={{ borderRight: "1px solid", borderColor: "secondary.swaGray2" }}>
        <Grid container sx={{ display: "flex", alignItems: "center", padding: "1rem" }}>
          <Grid item xs={2}>
            <Typography variant="h6" color="primary.swaBlue" fontWeight="bold" textAlign="left">
              DATs/FTOs
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <FormControl>
              <RadioGroup
                value={selectedAwardFilterRadioButton}
                onChange={handleAwardFilterRadioButtonClicked}
                row
                name="row-radio-buttons-group"
                sx={{ alignItems: "center" }}
              >
                <Typography sx={{ marginRight: "10px", fontWeight: "bold", color: "secondary.swaBlack" }}>
                  Show:{" "}
                </Typography>
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.ALL}
                  control={<Radio />}
                  label={`All (${props.datFtoAwardRows.length})`}
                />
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.UNAWARDED}
                  control={<Radio />}
                  label={`Available (${props.datFtoAwardRows.filter((line) => !line.eid).length})`}
                />
                <FormControlLabel
                  value={BID_AWARDED_FILTER_OPTIONS.AWARDED}
                  control={<Radio />}
                  label={`Awarded (${props.datFtoAwardRows.filter((line) => !!line.eid).length})`}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={1} sx={{ textAlign: "right", paddingRight: "1rem" }}>
            {isDialog ? (
              <StarsCloseIconButton onClick={handleEnlargeBidAllotmentsDialogClosed} />
            ) : (
              <IconButton sx={{ padding: 0 }} onClick={handleEnlargeBidAllotmentsButtonClicked}>
                <LaunchIcon color="primary" />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ display: "flex", textAlign: "left", backgroundColor: "secondary.swaLightestBlue" }}>
          <FormControl>
            <FormGroup row sx={{ alignItems: "center" }}>
              <Typography
                sx={{
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "secondary.swaBlack",
                  fontSize: "14px",
                  paddingLeft: "1rem",
                }}
              >
                Award Filter:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(TWU_DAT_FTO_BID_AWARD_TYPE.AUTO_AWARDED)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={TWU_DAT_FTO_BID_AWARD_TYPE.AUTO_AWARDED}
                  />
                }
                label={TWU_DAT_FTO_BID_AWARD_TYPE.AUTO_AWARDED}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedAwardFilterCheckBoxList.includes(TWU_DAT_FTO_BID_AWARD_TYPE.PARTIALLY_AWARDED)}
                    onChange={handleAwardFilterCheckBoxClicked}
                    value={TWU_DAT_FTO_BID_AWARD_TYPE.PARTIALLY_AWARDED}
                  />
                }
                label={TWU_DAT_FTO_BID_AWARD_TYPE.PARTIALLY_AWARDED}
              />
              {selectedAwardFilterCheckBoxList.length === 0 && (
                <FormHelperText error>At least one award type must be selected</FormHelperText>
              )}
            </FormGroup>
          </FormControl>
          <FormControl sx={{ borderLeft: "1px solid", borderColor: "secondary.swaLightBlue", flexGrow: 1 }}>
            <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
              <Typography
                sx={{
                  marginRight: "10px",
                  fontWeight: "bold",
                  color: "secondary.swaBlack",
                  fontSize: "14px",
                  paddingLeft: "1rem",
                }}
              >
                Time Off Type:
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTimeOffTypeFilterCheckBoxList.includes(TWU_DAT_FTO_BID_TIME_OFF_TYPES.DAT)}
                    onChange={handleTimeOffTypeFilterCheckBoxClicked}
                    value={TWU_DAT_FTO_BID_TIME_OFF_TYPES.DAT}
                  />
                }
                label={TWU_DAT_FTO_BID_TIME_OFF_TYPES.DAT}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTimeOffTypeFilterCheckBoxList.includes(TWU_DAT_FTO_BID_TIME_OFF_TYPES.FTO)}
                    onChange={handleTimeOffTypeFilterCheckBoxClicked}
                    value={TWU_DAT_FTO_BID_TIME_OFF_TYPES.FTO}
                  />
                }
                label={TWU_DAT_FTO_BID_TIME_OFF_TYPES.FTO}
              />
              {selectedTimeOffTypeFilterCheckBoxList.length === 0 && (
                <FormHelperText error>At least one time off type must be selected</FormHelperText>
              )}
            </FormGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              height: isDialog ? "80vh" : `calc(86vh - 240px)`,
              width: "inherit",
              "& .gray-color-cell": {
                backgroundColor: "secondary.swaGray3",
              },
            }}
            data-testid="bid-lines-grid"
          >
            <DataGridPro
              sx={{
                border: 0,
                "& div": {
                  "& .MuiDataGrid-footerContainer": {
                    display: "none",
                  },
                  "& .MuiDataGrid-cell": { whiteSpace: "normal !important", textAlign: "left" },
                },
                borderRadius: "0px",
                "& .MuiDataGrid-columnHeaderTitle": {
                  textOverflow: "clip",
                  whiteSpace: "break-spaces",
                  lineHeight: 1.2,
                  textAlign: "left",
                },
              }}
              columns={getDATFTOColumns({
                station: props.bid.station,
                handleAwardedNameClick: props.handleAwardedNameClick,
              })}
              getRowId={(row) => row.id}
              rows={filteredDATFTOAwardRows}
              rowHeight={60}
              components={{
                LoadingOverlay: customLoadingTableOverlay,
                NoRowsOverlay: CustomNoRowsTableOverlay,
                ErrorOverlay: CustomErrorTableOverlay,
              }}
              componentsProps={{
                noRowsOverlay: { pageType: BID_AWARD_NO_BID_PAGE_TYPE },
              }}
              loading={props.isBidAllotmentsLoading}
              disableColumnMenu={true}
              showColumnVerticalBorder={true}
              showCellVerticalBorder={true}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  const renderUnawardedSection = () => {
    return (
      <Grid container>
        <Box sx={{ width: "100%" }}>
          <Grid item xs={12} sx={{ padding: "1rem", textAlign: "left", marginTop: "8px" }}>
            <Typography variant="h6" color="primary.swaBlue" fontWeight={"bold"}>
              {`Unawarded (${props.unAwardedList.length})`}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              paddingLeft: "1rem",
              paddingTop: "10px",
              paddingBottom: "10px",
              display: "flex",
              backgroundColor: "secondary.swaLightestBlue",
            }}
          >
            <Typography sx={{ paddingRight: "30px" }}>
              Total: <strong>{props.unAwardedList.length}</strong>
            </Typography>
            <Typography sx={{ paddingRight: "30px" }}>
              NEC:{" "}
              <strong>
                {
                  props.unAwardedList.filter((emp) => emp.awardType === TWU_DAT_FTO_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES)
                    .length
                }
              </strong>
            </Typography>
            <Typography sx={{ paddingRight: "30px" }}>
              NB:{" "}
              <strong>
                {props.unAwardedList.filter((emp) => emp.awardType === TWU_DAT_FTO_BID_AWARD_TYPE.NO_BID).length}
              </strong>
            </Typography>
            <Typography sx={{ paddingRight: "30px" }}>
              NS:{" "}
              <strong>
                {props.unAwardedList.filter((emp) => emp.awardType === TWU_DAT_FTO_BID_AWARD_TYPE.NO_SCHEDULE).length}
              </strong>
            </Typography>
            <Typography>
              ZB:{" "}
              <strong>
                {props.unAwardedList.filter((emp) => emp.awardType === TWU_DAT_FTO_BID_AWARD_TYPE.ZERO_BALANCE).length}
              </strong>
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              paddingLeft: "1rem",
              paddingTop: "10px",
              paddingBottom: "10px",
              display: "flex",
              backgroundColor: "secondary.swaLightestBlue",
            }}
          >
            <FormControl>
              <FormGroup row sx={{ alignItems: "center", backgroundColor: "secondary.swaLightestBlue" }}>
                <Typography
                  sx={{
                    marginRight: "10px",
                    fontWeight: "bold",
                    color: "secondary.swaBlack",
                    fontSize: "14px",
                  }}
                >
                  Unawarded Filter:
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(
                        TWU_DAT_FTO_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES
                      )}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={TWU_DAT_FTO_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
                    />
                  }
                  label={TWU_DAT_FTO_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(TWU_DAT_FTO_BID_AWARD_TYPE.NO_BID)}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={TWU_DAT_FTO_BID_AWARD_TYPE.NO_BID}
                    />
                  }
                  label={TWU_DAT_FTO_BID_AWARD_TYPE.NO_BID}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(TWU_DAT_FTO_BID_AWARD_TYPE.NO_SCHEDULE)}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={TWU_DAT_FTO_BID_AWARD_TYPE.NO_SCHEDULE}
                    />
                  }
                  label={TWU_DAT_FTO_BID_AWARD_TYPE.NO_SCHEDULE}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedUnawardedFilterCheckBoxList.includes(TWU_DAT_FTO_BID_AWARD_TYPE.ZERO_BALANCE)}
                      onChange={handleUnawardedFilterCheckBoxClicked}
                      value={TWU_DAT_FTO_BID_AWARD_TYPE.ZERO_BALANCE}
                    />
                  }
                  label={TWU_DAT_FTO_BID_AWARD_TYPE.ZERO_BALANCE}
                />
                {selectedUnawardedFilterCheckBoxList.length === 0 && (
                  <FormHelperText error>At least one award type must be selected</FormHelperText>
                )}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                height: `calc(85vh - 230px)`,
                "& .gray-color-cell": {
                  backgroundColor: "secondary.swaGray3",
                },
              }}
            >
              <DataGridPro
                sx={{
                  border: 0,
                  "& div": {
                    "& .MuiDataGrid-footerContainer": {
                      display: "none",
                    },
                  },
                  borderRadius: "0px",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    textOverflow: "clip",
                    whiteSpace: "break-spaces",
                    lineHeight: 1.2,
                    textAlign: "left",
                  },
                }}
                columns={getUnAwardedTableColumns({
                  station: props.bid.station,
                  bidReferenceNumber: props.bid.referenceNumber,
                  bidStatus: props.bid.status,
                })}
                rows={filteredDATFTOUnawardedRows}
                rowHeight={60}
                components={{
                  LoadingOverlay: customLoadingTableOverlay,
                  NoRowsOverlay: CustomNoRowsTableOverlay,
                  ErrorOverlay: CustomErrorTableOverlay,
                }}
                componentsProps={{
                  noRowsOverlay: { pageType: BID_AWARD_NO_BID_PAGE_TYPE },
                }}
                loading={props.isBidAllotmentsLoading}
                disableColumnMenu={true}
                showColumnVerticalBorder={true}
                showCellVerticalBorder={true}
              />
            </Box>
          </Grid>
        </Box>
      </Grid>
    );
  };

  const renderEnlargeBidAllotmentsDialog = () => {
    return (
      <Dialog onClose={handleEnlargeBidAllotmentsDialogClosed} open={isEnlargeBidAllotmentsDialogOpen} fullScreen>
        {renderDATFTOs(true)}
      </Dialog>
    );
  };

  const getPublishButtonToolTipText = () => {
    if (getEligibleEmployeesWithInvalidBalanceCount(props.bidGroup) > 0 && props.isFormChanged) {
      return "Unable to publish. There are unsaved changes and Employees with invalid balances.";
    }

    if (getEligibleEmployeesWithInvalidBalanceCount(props.bidGroup) > 0) {
      return "Unable to publish. There are Employees with invalid balances.";
    }

    if (props.isFormChanged) {
      return "Unable to publish. There are unsaved changes.";
    }

    if (hasEmployeesMarkedNS(props.bidGroup)) {
      return "Unable to publish. There are Employees marked as No Schedule.";
    }

    return "";
  };

  const shouldDisablePublishButton = () => {
    return (
      props.isFormChanged ||
      props.isBidGroupTabChanged ||
      isAwardedBid(props.bid.status) ||
      getEligibleEmployeesWithInvalidBalanceCount(props.bidGroup) > 0 ||
      hasEmployeesMarkedNS(props.bidGroup)
    );
  };

  const onPublishSuccess = () => {
    setIsPublishConfirmationDialogOpen(false);
    setShowPublishLoader(false);
    setIsSuccessDialogOpen(true);
  };

  const onPublishError = () => {
    setPublishErrors("Unexpected Error");
    setIsPublishConfirmationDialogOpen(false);
    setShowPublishLoader(false);
    setIsSuccessDialogOpen(true);
  };

  const renderPublishButton = () => {
    return (
      <Button
        variant="contained"
        color="primary"
        component="label"
        onClick={() => setIsPublishConfirmationDialogOpen(true)}
        disabled={shouldDisablePublishButton()}
        sx={{ margin: "5px" }}
      >
        Publish
      </Button>
    );
  };

  const onPublishConfirm = async () => {
    setShowPublishLoader(true);
    const bidData = { ...props.bid };
    bidData.status = BID_STATUS.AWARDED;
    bidData.scheduledStatus = "";

    const allotmentsPayload = buildAllotmentsPayload(props.allotmentDayState, props.calcDailyAllotments);
    try {
      const s3ObjectName = await getS3ObjectName(bidData.pk, bidData.station);
      const res = await getPresignedUrl("put_object", "bid-data", s3ObjectName);
      const result = await res.json();
      if (result.presignedUrl) {
        const bidS3Data = buildTimeOffBidS3Data(userSession, bidData, allotmentsPayload, props.bidGroup, s3ObjectName);
        const response = await uploadFileToS3(result.presignedUrl, bidS3Data);
        if (response.status === 200) {
          const requestId = response.headers.get("x-amz-request-id");
          props.currentRequestId.current = requestId;
          await longPollingBidFetch(bidData.referenceNumber, userSession, requestId);
          onPublishSuccess();
        } else {
          const errorMessage = "Bad response from uploadFileToS3";
          console.error(errorMessage);
          throw Error(errorMessage);
        }
      } else {
        const errorMessage = "Bad response from getPresignedUrl";
        console.error(errorMessage);
        throw Error(errorMessage);
      }
    } catch (err) {
      console.error("Error in onPublishConfirm: ", err);
      onPublishError();
    }
  };

  const handleReRunAwardClicked = () => {
    setIsReRunAwardWarningDialogOpen(true);
  };

  const handleReRunAwardConfirm = () => {
    setIsReRunAwardWarningDialogOpen(false);
    const clearedDataResult = clearMTOBidAwardData(props.allotmentDayState, props.bidGroup);
    props.setAllotmentDayState(clearedDataResult.allotmentDayState);
    props.setBidGroup(clearedDataResult.bidGroup);
    handleRunAwardClicked(clearedDataResult.allotmentDayState, clearedDataResult.bidGroup);
  };

  const renderPostAwardResults = () => {
    return (
      <Grid container>
        {/* Station | Workgroup & Export/Print buttons */}
        <Grid
          item
          xs={6}
          sx={{
            borderBottom: "1px solid",
            borderColor: "secondary.swaGray2",
            paddingLeft: "1rem",
            paddingTop: "15px",
            paddingBottom: "15px",
          }}
        >
          <Typography variant="h6" sx={{ textAlign: "left", fontWeight: "bold" }}>
            {props.bid.station} | {props.bid.workgroup}
            {props.schedMonth !== "" &&
              props.schedYear !== "" &&
              ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
            {props.bidName && ` | ${props.bidName}`}
          </Typography>
        </Grid>
        <Grid
          item
          xs={6}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "right",
            paddingRight: "1rem",
            borderBottom: "1px solid",
            color: "secondary.swaGray2",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={isAwardRunning ? null : <ListAltOutlinedIcon />}
            component="label"
            onClick={handleReRunAwardClicked}
            disabled={
              isAwardRunning ||
              props.isBidGroupTabChanged ||
              props.isBidAllotmentsTabChanged ||
              isAwardedBid(props.bid.status) ||
              getEligibleEmployeesWithInvalidBalanceCount(props.bidGroup) > 0
            }
            sx={{ margin: "5px", width: "200px" }}
          >
            {isAwardRunning ? <CircularProgress size={25} /> : "Re-Run Award"}
          </Button>
          <TWUDATFTOBidAwardExport
            bid={props.bid}
            bidAllotments={props.datFtoAwardRows}
            schedMonth={props.schedMonth}
            schedYear={props.schedYear}
            calcDailyAllotments={props.calcDailyAllotments}
            isFormChanged={props.isFormChanged}
          />
          {getPublishButtonToolTipText() !== "" ? (
            <Tooltip title={getPublishButtonToolTipText()} arrow placement="bottom">
              <span>{renderPublishButton()}</span>
            </Tooltip>
          ) : (
            renderPublishButton()
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderBottom: "1px solid",
            borderColor: "secondary.swaGray2",
            paddingLeft: "1rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography variant="title" sx={{ paddingRight: "20px", fontWeight: "bold", color: "primary.swaBlue" }}>
            Key:
          </Typography>
          <Typography variant="title">
            <strong>AA: </strong> Auto-Awarded
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NEC: </strong> Not Enough Choices
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NB: </strong> No Bid
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>NS: </strong> No Schedule
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>PA: </strong> Partially Awarded
          </Typography>
          <Typography sx={{ paddingRight: "10px", paddingLeft: "10px" }}>|</Typography>
          <Typography variant="title">
            <strong>ZB: </strong> Zero Balance
          </Typography>
        </Grid>
        <Grid item xs={6.7}>
          {renderDATFTOs(false)}
        </Grid>
        <Grid item xs={5.3} sx={{ borderLeft: "5px solid", borderColor: "secondary.swaGray2" }}>
          {renderUnawardedSection()}
        </Grid>
      </Grid>
    );
  };

  return (
    <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
      {!props.isBidGroupLoading && !props.isBidAllotmentsLoading ? (
        <>
          {props.isBidGroupTabChanged && renderBidGroupChangesDetectedAlert()}
          {props.isBidAllotmentsTabChanged && renderBidAllotmentsChangesDetectedAlert()}
          {getEligibleEmployeesWithInvalidBalanceCount(props.bidGroup) > 0 && renderInvalidBalanceEmployeesAlert()}
          {isBidAwardAvailable() ? renderPostAwardResults() : renderPreAwardChecks()}
          {isEnlargeBidAllotmentsDialogOpen && renderEnlargeBidAllotmentsDialog()}
          {isReRunAwardWarningDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isReRunAwardWarningDialogOpen}
              title="Re-run Award"
              message="You are running the award again. This will delete the existing award results."
              handleNoButtonClicked={() => setIsReRunAwardWarningDialogOpen(false)}
              handleYesButtonClicked={handleReRunAwardConfirm}
              isYesLoading={isAwardRunning}
              noTitle="Cancel"
              yesTitle="Re-Run Award"
            />
          )}
          {isPublishConfirmationDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isPublishConfirmationDialogOpen}
              title="Publish TWU DAT/FTO Bid"
              message={TWU_DAT_FTO_BID_PUBLISH_DIALOG_MESSAGE(
                props.bid.station,
                props.bid.workgroup,
                props.schedMonth,
                props.schedYear
              )}
              handleNoButtonClicked={() => setIsPublishConfirmationDialogOpen(false)}
              handleYesButtonClicked={onPublishConfirm}
              isYesLoading={showPublishLoader}
              noTitle="Cancel"
              yesTitle="PUBLISH"
            />
          )}
          {isSuccessDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isSuccessDialogOpen}
              title={"Bid Confirmation"}
              message={"The bid has been successfully published!"}
              hasError={publishErrors !== ""}
              error={publishErrors}
              redirectTo={publishErrors === "" && props.bidAdminPath}
              handleOKClick={() => {
                setPublishErrors("");
                setIsSuccessDialogOpen(false);
                publishErrors !== "" && props.loadBidData();
              }}
            />
          )}
        </>
      ) : (
        <div style={{ width: "100%" }}>
          <LinearProgress />
        </div>
      )}
    </Grid>
  );
};

TWUDATFTOBidAward.propTypes = {
  bid: PropTypes.object.isRequired,
  bidGroup: PropTypes.array.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  isBidAllotmentsLoading: PropTypes.bool.isRequired,
  isBidGroupTabChanged: PropTypes.bool.isRequired,
  isBidAllotmentsTabChanged: PropTypes.bool.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  allotmentDayState: PropTypes.array.isRequired,
  calcDailyAllotments: PropTypes.object.isRequired,
  bidName: PropTypes.string,
  handleAwardedNameClick: PropTypes.func.isRequired,
  setAllotmentDayState: PropTypes.func.isRequired,
  setInitialAllotmentDayState: PropTypes.func.isRequired,
  setInitialBidGroup: PropTypes.func.isRequired,
  setBid: PropTypes.func.isRequired,
  setBidGroup: PropTypes.func.isRequired,
  unAwardedList: PropTypes.array.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  datFtoAwardRows: PropTypes.array.isRequired,
  currentRequestId: PropTypes.object.isRequired,
  bidAdminPath: PropTypes.string.isRequired,
  loadBidData: PropTypes.func.isRequired,
};

export default TWUDATFTOBidAward;
